// Map ISO 4217 numeric currency codes to alphabetic codes
const NUMERIC_TO_ALPHA: Record<string, string> = {
  "032": "ARS",
  "840": "USD",
  "978": "EUR",
  "986": "BRL",
  "152": "CLP",
  "170": "COP",
  "484": "MXN",
  "604": "PEN",
  "858": "UYU",
  "826": "GBP",
  "600": "PYG",
};

/**
 * Get the locale's decimal separator
 */
export function getDecimalSeparator(locale: string): string {
  const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
  return parts.find((p) => p.type === "decimal")?.value ?? ".";
}

/**
 * Get the locale's group (thousands) separator
 */
export function getGroupSeparator(locale: string): string {
  const parts = new Intl.NumberFormat(locale).formatToParts(10000);
  return parts.find((p) => p.type === "group")?.value ?? ",";
}

/**
 * Format a number for display using the browser locale and the currency code
 */
export function formatCurrencyAmount(value: number, currencyNumericCode: string, locale?: string): string {
  const resolvedLocale = locale ?? navigator.language;
  const alphaCurrency = NUMERIC_TO_ALPHA[currencyNumericCode];

  if (alphaCurrency) {
    try {
      return new Intl.NumberFormat(resolvedLocale, {
        style: "currency",
        currency: alphaCurrency,
        currencyDisplay: "narrowSymbol",
      }).format(value);
    } catch {
      // fallback without narrowSymbol
      return new Intl.NumberFormat(resolvedLocale, {
        style: "currency",
        currency: alphaCurrency,
      }).format(value);
    }
  }

  return new Intl.NumberFormat(resolvedLocale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a number value (no currency symbol) using proper locale separators
 */
export function formatAmountDisplay(value: number, currencyNumericCode: string, locale?: string): string {
  const resolvedLocale = locale ?? navigator.language;
  const alphaCurrency = NUMERIC_TO_ALPHA[currencyNumericCode];

  const decimals = getDecimalsForCurrency(alphaCurrency);

  return new Intl.NumberFormat(resolvedLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Parse a localized amount string back to a number
 */
export function parseLocalizedAmount(input: string, locale?: string): number {
  const resolvedLocale = locale ?? navigator.language;
  const decSep = getDecimalSeparator(resolvedLocale);
  const grpSep = getGroupSeparator(resolvedLocale);

  // Remove group separators, replace decimal separator with dot
  let normalized = input;
  if (grpSep) {
    normalized = normalized.replaceAll(grpSep, "");
  }
  if (decSep !== ".") {
    normalized = normalized.replace(decSep, ".");
  }

  return Number.parseFloat(normalized) || 0;
}

/**
 * Get number of decimal digits for a given currency
 */
function getDecimalsForCurrency(alpha?: string): number {
  if (!alpha) return 2;
  // Currencies with 0 decimals
  const zeroDecimals = ["CLP", "PYG", "JPY", "KRW"];
  if (zeroDecimals.includes(alpha)) return 0;
  return 2;
}

export function getAlphaCurrency(numericCode: string): string | undefined {
  return NUMERIC_TO_ALPHA[numericCode];
}

export function getDecimalsForCurrencyCode(numericCode: string): number {
  const alpha = NUMERIC_TO_ALPHA[numericCode];
  return getDecimalsForCurrency(alpha);
}
